/**
 * Vilka parter visas på en uthyrning?
 *
 * Frågor som tidigare besvarades var för sig i vyerna:
 *  - vilka ägare finns att välja mellan när en uthyrning registreras
 *  - vems namn ska visas på en redan registrerad uthyrning
 *  - vilket föreningsnamn ska visas när föreningen bytt namn sedan dess
 *
 * Alla hålls fria från Firestore så de kan testas direkt.
 */

import { normalizeAddress, normalizeApartmentNumber, normalizeZipCode } from './apartmentKey';
import { isPlaceholderOwnerName } from './ownerSnapshot';

const UNKNOWN_OWNER = 'Okänd ägare';

const trim = (value) => (value || '').trim();

/** Namnet som visas för en person eller juridisk person. */
export function formatOwnerLabel(person) {
  if (!person) return '';
  const legalEntity = trim(person.legalEntity);
  if (legalEntity) return legalEntity;
  return `${trim(person.firstName)} ${trim(person.lastName)}`.trim();
}

/**
 * Alla ägare som går att koppla en uthyrning till.
 *
 * @param {object|null} housing Housing-dokument.
 * @returns {Array<{id:number, firstName:string, lastName:string, email:string, legalEntity:string, label:string}>}
 */
export function listOwnerCandidates(housing) {
  const members = housing?.members;

  const source = Array.isArray(members) && members.length > 0
    ? members
    : housing && (housing.ownerFirstName || housing.ownerLastName || housing.ownerEmail)
      ? [{
        firstName: housing.ownerFirstName,
        lastName: housing.ownerLastName,
        email: housing.ownerEmail,
      }]
      : [];

  return source.map((person, index) => ({
    id: index,
    firstName: trim(person.firstName),
    lastName: trim(person.lastName),
    email: trim(person.email),
    legalEntity: trim(person.legalEntity),
    label: formatOwnerLabel(person),
  }));
}

/**
 * Sant när lägenheten har fler än en möjlig ägare — då måste admin välja
 * aktivt vem som ska stå på avtalet i stället för att formuläret gissar.
 */
export function requiresOwnerChoice(housing) {
  return listOwnerCandidates(housing).length > 1;
}

/**
 * Ägarnamnet som ska visas för en uthyrning.
 *
 * Ansökans egna ownerFirstName/ownerLastName är en ögonblicksbild från
 * registreringen och går alltid först — annars räknas namnet fram live ur
 * housing-datat och "ändras" när lägenheten byter ägare.
 *
 * @param {object} params
 * @param {object}   params.application Ansökningsdokument.
 * @param {object}   [params.housing]   Matchande housing-dokument.
 * @param {Function} params.fetchUser   async (uid) => användardokument | null.
 */
/**
 * Ägaren enligt medlemsregistret. Hopslagen ur dubbletter med olika medlemmar →
 * alla visas, annars den första. En definition, så listorna inte glider isär.
 */
export function housingOwnerLabel(housing) {
  if (!housing) return '';
  return housing.mergedFromDuplicates
    ? (housing.members || []).map(formatOwnerLabel).filter(Boolean).join(' / ')
    : formatOwnerLabel(housing.members && housing.members[0]);
}

export async function resolveOwnerName({ application, housing, fetchUser }) {
  const fromApplication = formatOwnerLabel({
    legalEntity: application?.ownerLegalEntity,
    firstName: application?.ownerFirstName,
    lastName: application?.ownerLastName,
  });
  // "Okänd Ägare" sparades förr som namn när ingen ägare valts. Det är en
  // platshållare, inte ett namn — låt medlemsregistret svara i stället.
  if (fromApplication && !isPlaceholderOwnerName(fromApplication)) return fromApplication;

  const lookup = async (uid) => {
    if (!uid) return '';
    try {
      return formatOwnerLabel(await fetchUser(uid));
    } catch (error) {
      return '';
    }
  };

  const fromOwnedBy = await lookup(housing?.ownedBy);
  if (fromOwnedBy) return fromOwnedBy;

  const fromMembers = housingOwnerLabel(housing);
  if (fromMembers) return fromMembers;

  const fromCreatedBy = await lookup(application?.createdBy);
  if (fromCreatedBy) return fromCreatedBy;

  return UNKNOWN_OWNER;
}

/**
 * Vilken lägenhet i medlemsregistret hör uthyrningen till?
 *
 * Vyerna jämförde förr adress och lägenhetsnummer tecken för tecken. Ett extra
 * mellanslag eller "Lgh 1101" mot "1101" räckte för att uthyrningen skulle tappa
 * sin lägenhet — och därmed både adress och ägare i listan. Här används samma
 * nyckel som överallt annars (`buildApartmentKey`).
 *
 * Dubbletter i registret: pekar alla på samma ägare finns ingen tvekan. Pekar de
 * på olika ägare gissar vi inte.
 */
export function pickHousingForApplication(housingList, application) {
  const list = housingList || [];
  if (!application) return null;

  if (application.housingId) {
    const byId = list.find((h) => h.id === application.housingId);
    if (byId) return byId;
  }

  // Adress + lägenhetsnummer måste stämma. Postnumret får bara SKILJA: manuellt
  // inlagda uthyrningar saknar ofta postnummer (Makrillen lgh 1203 i drift), och
  // då ska de ändå hitta sin lägenhet. Två olika postnummer är två olika lägenheter.
  const address = normalizeAddress(application.address);
  const apartment = normalizeApartmentNumber(application.apartmentNumber);
  if (!address || !apartment) return null; // aldrig en gissning på bara lägenhetsnummer
  const zip = normalizeZipCode(application.zipCode);
  const sameApartment = list.filter(
    (h) => normalizeAddress(h.address) === address && normalizeApartmentNumber(h.apartmentNumber) === apartment,
  );
  const zipOf = (h) => normalizeZipCode(h.zipCode);
  const exactZip = zip ? sameApartment.filter((h) => zipOf(h) === zip) : [];
  const candidates = exactZip.length > 0
    ? exactZip
    : sameApartment.filter((h) => !zip || !zipOf(h));
  if (candidates.length <= 1) return candidates[0] || null;

  // Tomma dubbletter säger inget om ägaren — bara de ifyllda får rösta.
  const withOwner = candidates.filter((h) => formatOwnerLabel(h.members && h.members[0]));
  if (withOwner.length === 0) return candidates[0];
  const owners = new Set(withOwner.map((h) => formatOwnerLabel(h.members[0]).toLowerCase()));
  if (owners.size === 1) return withOwner[0];

  // Olika medlemmar på samma lägenhet: ingen väljs framför den andra. Alla står
  // i registret, så alla visas. Inget konto (ownedBy) följer med — vi vet inte
  // vems det är.
  const seen = new Set();
  const members = [];
  withOwner.forEach((h) => (h.members || []).forEach((m) => {
    const key = formatOwnerLabel(m).toLowerCase();
    if (key && !seen.has(key)) { seen.add(key); members.push(m); }
  }));
  return { ...withOwner[0], ownedBy: null, members, mergedFromDuplicates: true };
}

/**
 * Ägarnamnet i styrelsens egen ansökningslista.
 *
 * Listan byggde förr namnet själv och tog den sparade platshållaren "Okänd
 * Ägare" för ett namn — styrelsen såg alltså samma fel som Hyria-admin, på varje
 * manuellt inlagd uthyrning. Ordningen: owners[0] → ansökans eget namn →
 * medlemsregistret → kontot i ownedBy (eller namnet, i äldre data).
 */
export async function resolveBoardOwnerName({ application, housingList, fetchUser }) {
  const first = application?.owners?.[0];
  const fromOwners = formatOwnerLabel(first);
  if (fromOwners && !isPlaceholderOwnerName(fromOwners)) return fromOwners;

  const housing = pickHousingForApplication(housingList, application);
  const resolved = await resolveOwnerName({ application, housing, fetchUser });
  if (resolved !== UNKNOWN_OWNER) return resolved;

  const ownedBy = trim(application?.ownedBy);
  if (!ownedBy) return UNKNOWN_OWNER;
  // Äldre data har ibland ägarens NAMN i ownedBy i stället för ett konto-id.
  const looksLikeUid = ownedBy.length > 20 && /^[a-zA-Z0-9]+$/.test(ownedBy);
  if (!looksLikeUid) return ownedBy;
  try {
    const user = await fetchUser(ownedBy);
    return formatOwnerLabel(user) || trim(user?.email) || UNKNOWN_OWNER;
  } catch (error) {
    return UNKNOWN_OWNER;
  }
}

/**
 * Ägarens namn och e-post som de ska VISAS — synkront, för vyer som redan har
 * underlaget. Ansökans egna fält först, sedan kontot. Finns inget underlag står
 * det "Okänd ägare" och ingen e-post: detaljrutan visade förr det påhittade
 * namnet "Anna Svensson" och anna@example.com i det läget.
 *
 * AddRentalModal sparar adminens e-post som reserv i ownerEmail. Den är inte
 * ägarens, så den visas inte.
 */
export function ownerDisplay({ application, user }) {
  const fromApplication = formatOwnerLabel({
    legalEntity: application?.ownerLegalEntity,
    firstName: application?.ownerFirstName,
    lastName: application?.ownerLastName,
  });
  // ownerName är ögonblicksbilden som skrivs när ansökan skapas (och som fylldes
  // i efterhand 14/9) — den gäller när för- och efternamn saknas.
  const snapshotName = trim(application?.ownerName);
  const name =
    (fromApplication && !isPlaceholderOwnerName(fromApplication) && fromApplication) ||
    (snapshotName && !isPlaceholderOwnerName(snapshotName) && snapshotName) ||
    formatOwnerLabel(user) ||
    UNKNOWN_OWNER;

  const applicationEmail = trim(application?.ownerEmail);
  const isAdminFallback =
    !!applicationEmail &&
    (applicationEmail === trim(application?.createdBy) || applicationEmail === 'admin@hyria.se');
  const email = (!isAdminFallback && applicationEmail) || trim(user?.email) || '';

  return { name, email };
}

/** Statusar som betyder att uthyrningen är avslutad och alltså historik. */
const CLOSED_STATUSES = new Set([
  'COMPLETED',
  'FINAL_REJECTED',
  'OWNER_REJECTED',
  'REJECTED',
]);

/**
 * Sant när uthyrningen är avslutad. En avslutad uthyrning är ett historiskt
 * faktum och ska visa parterna som de var då — inte som de är i dag.
 */
export function isHistoricalRental(application) {
  if (!application) return false;
  return (
    CLOSED_STATUSES.has(application.status) ||
    application.rentalStatus === 'COMPLETED'
  );
}

/**
 * Föreningsnamnet som ska visas för en uthyrning.
 *
 * Ansökan bär föreningens namn som det såg ut när uthyrningen registrerades
 * (`housingCooperative`). Byter föreningen namn skrev vyerna tidigare om även
 * avslutade uthyrningar, eftersom namnet slogs upp live vid varje visning.
 *
 * Avslutade uthyrningar visar därför ansökans egna namn, pågående visar det
 * aktuella. Saknas det ena används det andra.
 *
 * @param {object} params
 * @param {object} params.application Ansökningsdokument.
 * @param {object} [params.association] Föreningsdokumentet, live-uppslaget.
 * @returns {string} Namnet, eller tom sträng när inget finns.
 */
export function resolveAssociationName({ application, association }) {
  const fromApplication = trim(application?.housingCooperative);
  const live = trim(association?.name);

  return isHistoricalRental(application)
    ? fromApplication || live
    : live || fromApplication;
}
